import React, { useState, useRef, useEffect } from 'react';
import { ArrowRight, ArrowUpRight, Heart, Plus, Star, Quote, Truck, RefreshCw, Shield, Clock, Instagram, Leaf, Scissors, Play, ChevronLeft, ChevronRight, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * Template 7: VOGUE / ARTISAN
 * Mobile: Handcrafted, Artisanal, Bohemian (ARTISAN)
 * Desktop: High Fashion, Bold, Statement-making (VOGUE)
 */
const Template7 = () => {
    const navigate = useNavigate();
    const [activeTestimonial, setActiveTestimonial] = useState(0);
    const [wishlist, setWishlist] = useState({});
    const touchStartX = useRef(0);
    const [currentSlide, setCurrentSlide] = useState(0);
    const [currentLook, setCurrentLook] = useState(0);

    // Mobile testimonial auto-rotate
    useEffect(() => {
        const timer = setInterval(() => {
            setActiveTestimonial((prev) => (prev + 1) % mobileTestimonials.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    // Desktop hero auto-slide
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
        }, 5000);
        return () => clearInterval(timer);
    }, []);

    const handleTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
    const handleTouchEnd = (e) => {
        const diff = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(diff) > 50) {
            setActiveTestimonial((prev) =>
                diff > 0 ? (prev + 1) % mobileTestimonials.length : (prev - 1 + mobileTestimonials.length) % mobileTestimonials.length
            );
        }
    };

    const toggleWishlist = (id, e) => {
        e.stopPropagation();
        setWishlist((prev) => ({ ...prev, [id]: !prev[id] }));
    };

    // Mobile data (ARTISAN)
    const mobileCategories = [
        { name: 'Hand-block Prints', count: '180+ Pieces', image: 'https://images.unsplash.com/photo-1558171813-4c088753af8f?w=600&h=800&fit=crop' },
        { name: 'Natural Dye Collection', count: '120+ Styles', image: 'https://images.unsplash.com/photo-1594938298603-c8148c4dae35?w=600&h=800&fit=crop' },
        { name: 'Handwoven Textiles', count: '95+ Designs', image: 'https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=600&h=800&fit=crop' },
    ];

    const mobileProducts = [
        { id: 1, name: 'Indigo Block Print Dress', price: 3299, originalPrice: 4599, badge: 'BESTSELLER', image: 'https://images.unsplash.com/photo-1434389677669-e08b4cac3105?w=500&h=650&fit=crop' },
        { id: 2, name: 'Hand-embroidered Tunic', price: 2799, originalPrice: null, badge: 'NEW', image: 'https://images.unsplash.com/photo-1594463750939-ebb28c3f7f75?w=500&h=650&fit=crop' },
        { id: 3, name: 'Khadi Cotton Palazzo Set', price: 2199, originalPrice: 2999, badge: 'SALE', image: 'https://images.unsplash.com/photo-1583391733981-8b530e07ece4?w=500&h=650&fit=crop' },
        { id: 4, name: 'Ajrakh Print Wrap Skirt', price: 1899, originalPrice: null, badge: 'TRENDING', image: 'https://images.unsplash.com/photo-1583391733956-6c78276477e2?w=500&h=650&fit=crop' },
        { id: 5, name: 'Bagru Print Kaftan', price: 2499, originalPrice: 3499, badge: 'SALE', image: 'https://images.unsplash.com/photo-1594040226829-7f251ab46d80?w=500&h=650&fit=crop' },
        { id: 6, name: 'Handloom Linen Shirt', price: 1599, originalPrice: null, badge: 'NEW', image: 'https://images.unsplash.com/photo-1564257631407-4deb1f99d992?w=500&h=650&fit=crop' },
    ];

    const mobileTestimonials = [
        { text: 'I can actually feel the craft in every stitch. The block-printed dress has become my absolute favourite for weekend brunches.', name: 'Aditi Rawat', location: 'Jaipur', rating: 5 },
        { text: 'The natural dyes are gorgeous and the fabric gets softer with every wash. Supporting artisans has never felt this stylish.', name: 'Kavya Menon', location: 'Kochi', rating: 5 },
        { text: 'Perfect bohemian style with substance. I love knowing the person behind my clothes. Each piece has a story.', name: 'Nisha Sharma', location: 'Goa', rating: 5 },
    ];

    const mobileInstagramImages = [
        'https://images.unsplash.com/photo-1558171813-4c088753af8f?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1594938298603-c8148c4dae35?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1610030469983-98e550d6193c?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1583391733956-6c78276477e2?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1594040226829-7f251ab46d80?w=400&h=400&fit=crop',
        'https://images.unsplash.com/photo-1441984904996-e0b6ba687e04?w=400&h=400&fit=crop',
    ];

    // Desktop data (VOGUE)
    const heroSlides = [
        { title: 'Haute Couture', subtitle: 'Spring/Summer 2024', description: 'Where Indian artistry meets global luxury', image: 'https://placehold.co/1920x1080/FDF2F8/831843?text=Couture+Campaign', cta: 'View Collection' },
        { title: 'The Bridal Edit', subtitle: 'Exclusive Preview', description: 'Timeless elegance for your special day', image: 'https://placehold.co/1920x1080/FFF1F2/881337?text=Bridal+Campaign', cta: 'Explore Bridal' },
        { title: 'Festive Luxe', subtitle: 'Limited Edition', description: 'Celebrate in extraordinary style', image: 'https://placehold.co/1920x1080/FCE7F3/9D174D?text=Festive+Campaign', cta: 'Shop Festive' }
    ];

    const desktopCategories = [
        { name: 'Designer Sarees', description: 'Exclusive Weaves', image: 'https://placehold.co/500x700/FDF2F8/831843?text=Sarees', count: '200+' },
        { name: 'Bridal Lehengas', description: 'Couture Collection', image: 'https://placehold.co/500x700/FFF1F2/881337?text=Lehengas', count: '150+' },
        { name: 'Gowns', description: 'Red Carpet Ready', image: 'https://placehold.co/500x700/FCE7F3/9D174D?text=Gowns', count: '80+' },
        { name: 'Anarkalis', description: 'Statement Pieces', image: 'https://placehold.co/500x700/FDF2F8/831843?text=Anarkalis', count: '120+' }
    ];

    const desktopProducts = [
        { id: 1, name: 'Hand-embroidered Silk Lehenga', designer: 'By Ritu Kumar', price: '\u20b91,25,000', image: 'https://placehold.co/400x550/FDF2F8/831843?text=Lehenga+1', isNew: true },
        { id: 2, name: 'Banarasi Tissue Saree', designer: 'By Sabyasachi', price: '\u20b985,000', image: 'https://placehold.co/400x550/FFF1F2/881337?text=Saree+1', isNew: false },
        { id: 3, name: 'Zardozi Work Anarkali', designer: 'By Manish Malhotra', price: '\u20b978,500', image: 'https://placehold.co/400x550/FCE7F3/9D174D?text=Anarkali+1', isNew: true },
        { id: 4, name: 'Crystal Embellished Gown', designer: 'By Tarun Tahiliani', price: '\u20b91,45,000', image: 'https://placehold.co/400x550/FDF2F8/831843?text=Gown+1', isNew: false },
        { id: 5, name: 'Kalamkari Printed Lehenga', designer: 'By Anita Dongre', price: '\u20b992,000', image: 'https://placehold.co/400x550/FFF1F2/881337?text=Lehenga+2', isNew: true },
        { id: 6, name: 'Chikankari Palazzo Set', designer: 'By Abu Jani Sandeep', price: '\u20b965,000', image: 'https://placehold.co/400x550/FCE7F3/9D174D?text=Palazzo+1', isNew: false }
    ];

    const lookbook = [
        { image: 'https://placehold.co/600x900/FDF2F8/831843?text=Look+1', title: 'The Modern Maharani' },
        { image: 'https://placehold.co/600x900/FFF1F2/881337?text=Look+2', title: 'Festive Opulence' },
        { image: 'https://placehold.co/600x900/FCE7F3/9D174D?text=Look+3', title: 'Evening Glamour' },
        { image: 'https://placehold.co/600x900/FDF2F8/831843?text=Look+4', title: 'Bridal Dreams' }
    ];

    const desktopTestimonials = [
        { text: 'The craftsmanship of my bridal lehenga was beyond extraordinary. Every detail was perfect for my Mumbai wedding.', name: 'Aishwarya Mehta', location: 'Mumbai', event: 'Bride, Dec 2024' },
        { text: 'As a celebrity stylist, I only recommend the best to my clients. This collection sets the standard for Indian luxury fashion.', name: 'Rhea Kapoor', location: 'Delhi', event: 'Celebrity Stylist' },
        { text: 'The fusion of traditional techniques with contemporary design is remarkable. Truly wearable art.', name: 'Deepika Singhania', location: 'Jaipur', event: 'Fashion Collector' }
    ];

    const designers = [
        { name: 'Sabyasachi Mukherjee', specialty: 'Bridal Couture', image: 'https://placehold.co/200x200/FDF2F8/831843?text=S' },
        { name: 'Manish Malhotra', specialty: 'Celebrity Fashion', image: 'https://placehold.co/200x200/FFF1F2/881337?text=M' },
        { name: 'Anita Dongre', specialty: 'Sustainable Luxury', image: 'https://placehold.co/200x200/FCE7F3/9D174D?text=A' },
        { name: 'Ritu Kumar', specialty: 'Heritage Textiles', image: 'https://placehold.co/200x200/FDF2F8/831843?text=R' }
    ];

    return (
        <div className="min-h-screen overflow-x-hidden font-sans">

            {/* ========== MOBILE WRAPPER (ARTISAN) ========== */}
            <div className="lg:hidden bg-stone-50 text-stone-800">

                {/* ANNOUNCEMENT BAR */}
                <div className="bg-amber-900 text-amber-50 py-2.5 text-center">
                    <p className="text-[11px] sm:text-xs tracking-[0.15em] uppercase">Handcrafted With Love &bull; Free Shipping Above Rs.1,499 &bull; Supporting 200+ Artisan Families</p>
                </div>

                {/* HERO - Mobile Stacked */}
                <section>
                    <div className="relative aspect-[4/5]">
                        <img src="https://images.unsplash.com/photo-1558171813-4c088753af8f?w=1920&q=80" alt="Artisan Collection" className="w-full h-full object-cover" />
                    </div>
                    <div className="bg-stone-50 px-5 py-8 text-center">
                        <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-100 rounded-full mb-4">
                            <Scissors className="w-3.5 h-3.5 text-amber-800" />
                            <span className="text-[11px] text-amber-800 font-medium tracking-wide uppercase">Handcrafted</span>
                        </div>
                        <h1 className="text-2xl sm:text-3xl font-light text-stone-800 mb-3 leading-tight">
                            Made by <span className="italic font-serif text-amber-800">Hands</span> That Care
                        </h1>
                        <p className="text-[14px] text-stone-500 mb-6 max-w-sm mx-auto leading-relaxed">
                            Earthy, bohemian fashion handcrafted by Indian artisans using age-old techniques and natural materials.
                        </p>
                        <button onClick={() => navigate('/template7/shop')} className="w-full bg-amber-900 text-white py-4 text-sm tracking-[0.15em] uppercase font-medium min-h-[48px] rounded-full">
                            Shop Handcrafted
                        </button>
                    </div>
                </section>

                {/* CATEGORIES - Mobile horizontal scroll */}
                <section className="py-10 sm:py-16 bg-white">
                    <div className="px-5 sm:px-6 mb-6">
                        <span className="text-[11px] sm:text-xs tracking-[0.2em] uppercase text-amber-800 font-medium">Explore Crafts</span>
                        <h2 className="text-xl sm:text-2xl font-light text-stone-800 mt-1">Shop By Craft</h2>
                    </div>
                    <div className="relative">
                        <div className="flex overflow-x-auto gap-3 px-5 snap-x snap-mandatory scrollbar-hide pb-4">
                            {mobileCategories.map((cat, i) => (
                                <div key={i} onClick={() => navigate('/template7/shop')} className="flex-none w-[68vw] snap-start cursor-pointer group">
                                    <div className="relative aspect-[3/4] rounded-2xl overflow-hidden">
                                        <img src={cat.image} alt={cat.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                        <div className="absolute inset-0 bg-gradient-to-t from-stone-900/60 via-transparent to-transparent" />
                                        <div className="absolute bottom-4 left-4 right-4">
                                            <h3 className="text-white text-lg font-medium">{cat.name}</h3>
                                            <p className="text-white/70 text-xs mt-0.5">{cat.count}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="absolute right-0 top-0 bottom-4 w-12 bg-gradient-to-l from-white to-transparent pointer-events-none" />
                    </div>
                </section>

                {/* PRODUCTS - Mobile horizontal scroll */}
                <section className="py-10 sm:py-16 bg-stone-50">
                    <div className="px-5 sm:px-6 flex items-end justify-between mb-6">
                        <div>
                            <span className="text-[11px] sm:text-xs tracking-[0.2em] uppercase text-amber-800 font-medium">Fresh From the Loom</span>
                            <h2 className="text-xl sm:text-2xl font-light text-stone-800 mt-1">New Arrivals</h2>
                        </div>
                    </div>
                    <div className="flex overflow-x-auto gap-3 px-5 snap-x snap-mandatory scrollbar-hide pb-4">
                        {mobileProducts.map((product) => (
                            <div key={product.id} onClick={() => navigate('/template7/shop')} className="flex-none w-[44vw] snap-start cursor-pointer group">
                                <div className="relative aspect-[3/4] rounded-2xl overflow-hidden mb-2 bg-amber-50/50">
                                    <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                                    {product.badge && (
                                        <span className={`absolute top-2 left-2 px-2 py-0.5 text-[10px] font-semibold tracking-wide rounded-full ${product.badge === 'SALE' ? 'bg-rose-600 text-white' : product.badge === 'NEW' ? 'bg-amber-800 text-white' : product.badge === 'BESTSELLER' ? 'bg-amber-600 text-white' : 'bg-stone-700 text-white'}`}>
                                            {product.badge}
                                        </span>
                                    )}
                                    <button onClick={(e) => toggleWishlist(product.id, e)} className="absolute top-2 right-2 w-8 h-8 bg-white/90 rounded-full flex items-center justify-center">
                                        <Heart className={`w-3.5 h-3.5 ${wishlist[product.id] ? 'fill-rose-500 text-rose-500' : 'text-stone-600'}`} />
                                    </button>
                                    <button className="absolute bottom-2 right-2 w-8 h-8 bg-amber-900/90 rounded-full flex items-center justify-center">
                                        <Plus className="w-3.5 h-3.5 text-white" />
                                    </button>
                                </div>
                                <h3 className="text-[13px] font-medium text-stone-800 line-clamp-1">{product.name}</h3>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                    <span className="text-[13px] font-semibold text-stone-800">Rs.{product.price.toLocaleString()}</span>
                                    {product.originalPrice && <span className="text-[11px] text-stone-400 line-through">Rs.{product.originalPrice.toLocaleString()}</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="px-5 mt-4">
                        <button onClick={() => navigate('/template7/shop')} className="w-full py-3.5 border border-amber-900 text-amber-900 text-sm tracking-[0.1em] uppercase font-medium min-h-[48px] rounded-full">
                            View All Products
                        </button>
                    </div>
                </section>

                {/* BRAND STORY - Mobile */}
                <section className="py-10 sm:py-16 bg-amber-900 text-white">
                    <div>
                        <div className="sm:rounded-2xl sm:mx-6 overflow-hidden">
                            <img src="https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800&h=1000&fit=crop" alt="Artisan at Work" className="w-full aspect-[4/5] object-cover" />
                        </div>
                        <div className="px-5 sm:px-6 py-8">
                            <span className="text-[11px] sm:text-xs tracking-[0.2em] uppercase text-amber-300 font-medium">Behind the Craft</span>
                            <h2 className="text-xl sm:text-2xl font-light text-white mt-2 mb-4 leading-snug">
                                Every Stitch Tells a <span className="italic font-serif text-amber-200">Story</span>
                            </h2>
                            <p className="text-[14px] sm:text-base text-amber-100/80 leading-relaxed mb-4">
                                We partner with artisan families across Rajasthan, Gujarat, and Maharashtra, preserving craft traditions
                                that date back centuries.
                            </p>
                            <p className="text-[14px] sm:text-base text-amber-200/60 leading-relaxed mb-6">
                                No machines. No shortcuts. Just honest craftsmanship and natural materials that feel as good as they look.
                            </p>
                            <div className="grid grid-cols-3 gap-4 py-6 border-y border-amber-800/50 mb-6">
                                <div className="text-center"><p className="text-2xl font-light text-white">200+</p><p className="text-xs text-amber-300/70 mt-0.5">Artisan Families</p></div>
                                <div className="text-center"><p className="text-2xl font-light text-white">15</p><p className="text-xs text-amber-300/70 mt-0.5">Craft Techniques</p></div>
                                <div className="text-center"><p className="text-2xl font-light text-white">8</p><p className="text-xs text-amber-300/70 mt-0.5">Indian States</p></div>
                            </div>
                            <button onClick={() => navigate('/template7/shop')} className="group w-full sm:w-auto inline-flex items-center justify-center gap-3 px-8 py-3.5 bg-amber-50 text-amber-900 text-sm font-medium tracking-wider uppercase hover:bg-white transition-colors min-h-[48px] rounded-full">
                                Meet Our Artisans <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </button>
                        </div>
                    </div>
                </section>

                {/* TESTIMONIALS - Mobile swipeable */}
                <section className="py-10 sm:py-16 bg-white">
                    <div className="px-5 sm:px-6">
                        <div className="text-center mb-6">
                            <span className="text-[11px] sm:text-xs tracking-[0.2em] uppercase text-amber-800 font-medium">Happy Hearts</span>
                            <h2 className="text-xl sm:text-2xl font-light text-stone-800 mt-1">Craft Lovers Speak</h2>
                        </div>
                        <div className="overflow-hidden" onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}>
                            <div className="flex transition-transform duration-500 ease-out" style={{ transform: `translateX(-${activeTestimonial * 100}%)` }}>
                                {mobileTestimonials.map((t, i) => (
                                    <div key={i} className="w-full flex-none px-1">
                                        <div className="bg-amber-50/50 rounded-2xl p-6">
                                            <div className="flex gap-0.5 mb-3">{[...Array(t.rating)].map((_, j) => <Star key={j} className="w-3.5 h-3.5 fill-amber-500 text-amber-500" />)}</div>
                                            <p className="text-[14px] text-stone-600 leading-relaxed mb-4 italic">"{t.text}"</p>
                                            <div className="border-t border-stone-200 pt-3">
                                                <p className="text-sm font-medium text-stone-800">{t.name}</p>
                                                <p className="text-xs text-stone-500">{t.location}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="flex justify-center gap-2 mt-4">
                            {mobileTestimonials.map((_, i) => (
                                <button key={i} onClick={() => setActiveTestimonial(i)} className={`h-1.5 rounded-full transition-all duration-300 ${activeTestimonial === i ? 'w-6 bg-amber-800' : 'w-1.5 bg-stone-300'}`} />
                            ))}
                        </div>
                    </div>
                </section>

                {/* FEATURES - Mobile horizontal scroll */}
                <section className="py-10 sm:py-16 bg-stone-50">
                    <div className="flex overflow-x-auto gap-3 px-5 snap-x snap-mandatory scrollbar-hide pb-2">
                        {[
                            { icon: Leaf, title: '100% Natural', desc: 'Organic and natural fabrics' },
                            { icon: Truck, title: 'Free Delivery', desc: 'On orders above Rs.1,499' },
                            { icon: RefreshCw, title: 'Easy Returns', desc: '15-day no-hassle returns' },
                            { icon: Shield, title: 'Artisan Verified', desc: 'Genuine handcrafted pieces' },
                        ].map((feature, i) => (
                            <div key={i} className="flex-none w-[55vw] snap-start flex items-start gap-3 bg-white rounded-2xl p-4 shadow-sm">
                                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                                    <feature.icon className="w-4 h-4 text-amber-800" />
                                </div>
                                <div>
                                    <p className="text-sm font-medium text-stone-800">{feature.title}</p>
                                    <p className="text-xs text-stone-500 mt-0.5">{feature.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* NEWSLETTER - Mobile */}
                <section className="py-10 sm:py-16 bg-amber-800 text-white">
                    <div className="px-5 sm:px-6 text-center">
                        <Scissors className="w-6 h-6 text-amber-300 mx-auto mb-4" />
                        <h2 className="text-xl sm:text-2xl font-light mb-3">Join the Craft Circle</h2>
                        <p className="text-[14px] sm:text-base text-amber-100/80 mb-6">
                            Artisan stories, new craft drops, and 10% off your first handcrafted piece.
                        </p>
                        <form className="flex flex-col gap-3 max-w-md mx-auto">
                            <input type="email" placeholder="your@email.com" className="flex-1 px-5 py-3.5 bg-amber-700/50 border border-amber-600/50 rounded-xl text-white placeholder-amber-300/60 focus:outline-none focus:border-amber-400 transition-colors" />
                            <button type="submit" className="px-8 py-3.5 bg-amber-50 text-amber-900 text-sm tracking-[0.1em] uppercase font-medium rounded-xl hover:bg-white transition-colors min-h-[48px]">Subscribe</button>
                        </form>
                        <p className="text-amber-400/60 text-[11px] sm:text-xs mt-4">No spam. Just good craft and earthy vibes.</p>
                    </div>
                </section>

                {/* INSTAGRAM - Mobile 3-col */}
                <section className="py-10 sm:py-16 bg-white">
                    <div className="text-center mb-6 px-5">
                        <div className="flex items-center justify-center gap-2 mb-1">
                            <Instagram className="w-5 h-5 text-amber-800" />
                            <span className="text-sm text-amber-800 font-medium">@artisancraft</span>
                        </div>
                        <h2 className="text-xl sm:text-2xl font-light text-stone-800">#HandmadeWithLove</h2>
                    </div>
                    <div className="grid grid-cols-3 gap-0.5">
                        {mobileInstagramImages.map((img, i) => (
                            <div key={i} className="relative aspect-square overflow-hidden group cursor-pointer">
                                <img src={img} alt={`Post ${i + 1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                                <div className="absolute inset-0 bg-amber-900/0 group-hover:bg-amber-900/30 transition-colors flex items-center justify-center">
                                    <Heart className="w-5 h-5 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

            </div>

            {/* ========== DESKTOP WRAPPER (ORIGINAL VOGUE THEME) ========== */}
            <div className="hidden lg:block bg-white font-sans">

                {/* Hero - Full Screen Slider */}
                <section className="relative h-screen overflow-hidden">
                    {heroSlides.map((slide, index) => (
                        <div key={index} className={`absolute inset-0 transition-opacity duration-1000 ${currentSlide === index ? 'opacity-100 z-10' : 'opacity-0 z-0'}`}>
                            <img src={slide.image} alt={slide.title} className="w-full h-full object-cover" />
                            <div className="absolute inset-0 bg-gradient-to-r from-rose-950/60 via-rose-900/30 to-transparent" />
                            <div className="absolute inset-0 flex items-center">
                                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
                                    <div className="max-w-2xl">
                                        <span className="inline-block text-rose-200 text-sm font-medium tracking-[0.3em] uppercase mb-4">{slide.subtitle}</span>
                                        <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-extralight text-white leading-[0.9] mb-6 tracking-tight">
                                            {slide.title.split(' ').map((word, i) => (
                                                <span key={i} className={i === 1 ? 'font-serif italic block' : 'block'}>{word}</span>
                                            ))}
                                        </h1>
                                        <p className="text-rose-100 text-lg lg:text-xl mb-8 max-w-md leading-relaxed">{slide.description}</p>
                                        <button onClick={() => navigate('/template7/shop')} className="group inline-flex items-center gap-3 px-8 py-4 bg-white text-rose-900 text-sm font-semibold tracking-wider uppercase hover:bg-rose-50 transition-all duration-300">
                                            {slide.cta} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                    <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20 flex gap-3">
                        {heroSlides.map((_, index) => (
                            <button key={index} onClick={() => setCurrentSlide(index)} className={`h-1 rounded-full transition-all duration-300 ${currentSlide === index ? 'w-12 bg-white' : 'w-6 bg-white/40 hover:bg-white/60'}`} />
                        ))}
                    </div>
                    <button onClick={() => setCurrentSlide((prev) => (prev - 1 + heroSlides.length) % heroSlides.length)} className="absolute left-4 lg:left-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-all">
                        <ChevronLeft className="w-5 h-5" />
                    </button>
                    <button onClick={() => setCurrentSlide((prev) => (prev + 1) % heroSlides.length)} className="absolute right-4 lg:right-8 top-1/2 -translate-y-1/2 z-20 w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-all">
                        <ChevronRight className="w-5 h-5" />
                    </button>
                </section>

                {/* Brand Statement */}
                <section className="py-20 lg:py-28 bg-gradient-to-b from-rose-50/50 to-white">
                    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <Sparkles className="w-8 h-8 text-rose-400 mx-auto mb-6" />
                        <h2 className="text-3xl lg:text-4xl xl:text-5xl font-extralight text-rose-950 leading-relaxed mb-6">
                            "Fashion is not just about clothes. It's about <span className="font-serif italic text-rose-600">heritage</span>,
                            <span className="font-serif italic text-rose-600"> craftsmanship</span>, and the
                            <span className="font-serif italic text-rose-600"> story</span> each piece tells."
                        </h2>
                        <p className="text-rose-700/70 text-lg max-w-2xl mx-auto">Curating India's finest designers and artisans for the discerning fashion connoisseur</p>
                    </div>
                </section>

                {/* Categories */}
                <section className="py-16 lg:py-24 bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex lg:items-end lg:justify-between mb-12 lg:mb-16">
                            <div>
                                <span className="text-rose-500 text-sm font-medium tracking-[0.2em] uppercase">The Collections</span>
                                <h2 className="text-3xl lg:text-5xl font-extralight text-rose-950 mt-3">Shop By Category</h2>
                            </div>
                            <button onClick={() => navigate('/template7/shop')} className="group flex items-center gap-2 text-rose-700 font-medium hover:text-rose-900 transition-colors">
                                View All <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                            </button>
                        </div>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                            {desktopCategories.map((category, index) => (
                                <div key={index} onClick={() => navigate('/template7/shop')} className="group cursor-pointer relative overflow-hidden">
                                    <div className="aspect-[5/7] overflow-hidden">
                                        <img src={category.image} alt={category.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                                    </div>
                                    <div className="absolute inset-0 bg-gradient-to-t from-rose-950/80 via-rose-900/20 to-transparent" />
                                    <div className="absolute bottom-0 left-0 right-0 p-4 lg:p-6">
                                        <p className="text-rose-200 text-xs tracking-wider uppercase mb-1">{category.description}</p>
                                        <h3 className="text-white text-lg lg:text-xl font-medium">{category.name}</h3>
                                        <p className="text-rose-300 text-sm mt-2">{category.count} Styles</p>
                                    </div>
                                    <div className="absolute top-4 right-4 w-10 h-10 bg-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-75 group-hover:scale-100">
                                        <ArrowUpRight className="w-4 h-4 text-rose-900" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Designer Spotlight */}
                <section className="py-16 lg:py-24 bg-rose-50/50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-12 lg:mb-16">
                            <span className="text-rose-500 text-sm font-medium tracking-[0.2em] uppercase">Featured Designers</span>
                            <h2 className="text-3xl lg:text-4xl font-extralight text-rose-950 mt-3">India's Finest Couturiers</h2>
                        </div>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
                            {designers.map((designer, index) => (
                                <div key={index} className="group text-center cursor-pointer" onClick={() => navigate('/template7/shop')}>
                                    <div className="relative w-32 h-32 lg:w-40 lg:h-40 mx-auto mb-4 overflow-hidden rounded-full bg-white shadow-lg group-hover:shadow-xl transition-shadow">
                                        <img src={designer.image} alt={designer.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                                    </div>
                                    <h3 className="text-rose-950 font-medium text-lg group-hover:text-rose-600 transition-colors">{designer.name}</h3>
                                    <p className="text-rose-600/70 text-sm mt-1">{designer.specialty}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Featured Products */}
                <section className="py-16 lg:py-24 bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex lg:items-end lg:justify-between mb-12 lg:mb-16">
                            <div>
                                <span className="text-rose-500 text-sm font-medium tracking-[0.2em] uppercase">The Edit</span>
                                <h2 className="text-3xl lg:text-5xl font-extralight text-rose-950 mt-3">New Arrivals</h2>
                            </div>
                            <button onClick={() => navigate('/template7/shop')} className="group flex items-center gap-2 text-rose-700 font-medium hover:text-rose-900 transition-colors">
                                Shop All New <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </button>
                        </div>
                        <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-8">
                            {desktopProducts.map((product) => (
                                <div key={product.id} onClick={() => navigate('/template7/shop')} className="group cursor-pointer">
                                    <div className="relative aspect-[4/5.5] overflow-hidden bg-rose-50 mb-4">
                                        <img src={product.image} alt={product.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                                        {product.isNew && <span className="absolute top-4 left-4 px-3 py-1 bg-rose-600 text-white text-xs font-medium tracking-wider uppercase">New</span>}
                                        <button className="absolute top-4 right-4 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 hover:bg-white hover:text-rose-600">
                                            <Heart className="w-5 h-5" />
                                        </button>
                                        <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-rose-950/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                            <button className="w-full py-3 bg-white text-rose-900 text-sm font-semibold uppercase tracking-wider hover:bg-rose-50 transition-colors">Quick View</button>
                                        </div>
                                    </div>
                                    <div className="space-y-1">
                                        <p className="text-rose-500 text-xs tracking-wider uppercase">{product.designer}</p>
                                        <h3 className="text-rose-950 font-medium group-hover:text-rose-600 transition-colors line-clamp-1">{product.name}</h3>
                                        <p className="text-rose-900 font-semibold text-lg">{product.price}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Lookbook */}
                <section className="py-16 lg:py-24 bg-rose-950 text-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="flex lg:items-end lg:justify-between mb-12 lg:mb-16">
                            <div>
                                <span className="text-rose-300 text-sm font-medium tracking-[0.2em] uppercase">Style Inspiration</span>
                                <h2 className="text-3xl lg:text-5xl font-extralight text-white mt-3">The Lookbook</h2>
                            </div>
                            <button className="group flex items-center gap-2 text-rose-300 font-medium hover:text-white transition-colors">
                                View Full Lookbook <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                            </button>
                        </div>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
                            {lookbook.map((look, index) => (
                                <div key={index} className="group relative aspect-[2/3] overflow-hidden cursor-pointer">
                                    <img src={look.image} alt={look.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                                    <div className="absolute inset-0 bg-gradient-to-t from-rose-950/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                    <div className="absolute bottom-0 left-0 right-0 p-4 transform translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                                        <p className="text-white text-sm font-medium">{look.title}</p>
                                    </div>
                                    <div className="absolute top-4 right-4 w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                                        <Play className="w-4 h-4 text-white ml-0.5" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Testimonials */}
                <section className="py-16 lg:py-24 bg-gradient-to-b from-white to-rose-50/50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-12 lg:mb-16">
                            <span className="text-rose-500 text-sm font-medium tracking-[0.2em] uppercase">Client Stories</span>
                            <h2 className="text-3xl lg:text-4xl font-extralight text-rose-950 mt-3">Voices of Elegance</h2>
                        </div>
                        <div className="grid md:grid-cols-3 gap-6 lg:gap-8">
                            {desktopTestimonials.map((testimonial, index) => (
                                <div key={index} className="bg-white p-8 lg:p-10 shadow-sm hover:shadow-lg transition-shadow duration-300 border-t-2 border-rose-200">
                                    <Quote className="w-10 h-10 text-rose-200 mb-6" />
                                    <p className="text-rose-800 leading-relaxed mb-8 text-lg italic">"{testimonial.text}"</p>
                                    <div className="flex items-center gap-4">
                                        <div className="w-14 h-14 bg-rose-100 rounded-full flex items-center justify-center">
                                            <span className="text-rose-600 font-semibold text-lg">{testimonial.name[0]}</span>
                                        </div>
                                        <div>
                                            <p className="font-medium text-rose-950">{testimonial.name}</p>
                                            <p className="text-rose-600/70 text-sm">{testimonial.event}</p>
                                            <p className="text-rose-400 text-xs">{testimonial.location}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Brand Story */}
                <section className="py-16 lg:py-24 bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                            <div className="relative">
                                <div className="aspect-[4/5] overflow-hidden">
                                    <img src="https://placehold.co/600x750/FDF2F8/831843?text=Atelier" alt="Fashion Atelier" className="w-full h-full object-cover" />
                                </div>
                                <div className="absolute -bottom-8 -right-8 w-48 h-48 bg-rose-100 -z-10" />
                                <div className="absolute top-8 -left-8 w-32 h-32 border-2 border-rose-200 -z-10" />
                            </div>
                            <div>
                                <span className="text-rose-500 text-sm font-medium tracking-[0.2em] uppercase">Our Story</span>
                                <h2 className="text-3xl lg:text-4xl font-extralight text-rose-950 mt-3 mb-6">Where Tradition Meets <span className="font-serif italic">Modernity</span></h2>
                                <p className="text-rose-700/80 text-lg leading-relaxed mb-6">
                                    Founded in Mumbai in 2010, VOGUE has been at the forefront of redefining Indian luxury fashion.
                                    We curate the finest designer collections, working directly with master artisans and couturiers.
                                </p>
                                <p className="text-rose-700/80 leading-relaxed mb-8">
                                    From bridal trousseau to red carpet moments, we dress India's most discerning fashion connoisseurs.
                                </p>
                                <div className="grid grid-cols-3 gap-8 mb-8 py-8 border-y border-rose-100">
                                    <div className="text-center"><p className="text-3xl lg:text-4xl font-light text-rose-600">50+</p><p className="text-rose-700/70 text-sm mt-1">Designers</p></div>
                                    <div className="text-center"><p className="text-3xl lg:text-4xl font-light text-rose-600">10k+</p><p className="text-rose-700/70 text-sm mt-1">Brides Dressed</p></div>
                                    <div className="text-center"><p className="text-3xl lg:text-4xl font-light text-rose-600">15</p><p className="text-rose-700/70 text-sm mt-1">Years of Excellence</p></div>
                                </div>
                                <button onClick={() => navigate('/template7/shop')} className="group inline-flex items-center gap-3 text-rose-700 font-medium hover:text-rose-900 transition-colors">
                                    Learn More About Us <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                                </button>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Newsletter */}
                <section className="py-16 lg:py-24 bg-gradient-to-br from-rose-100 via-rose-50 to-pink-50">
                    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <Sparkles className="w-8 h-8 text-rose-400 mx-auto mb-6" />
                        <h2 className="text-3xl lg:text-4xl font-extralight text-rose-950 mb-4">Join the Inner Circle</h2>
                        <p className="text-rose-700/80 text-lg mb-8">Be the first to access exclusive collections, private sales, and fashion events. Plus, enjoy 10% off your first purchase.</p>
                        <div className="flex flex-col sm:flex-row gap-3 max-w-lg mx-auto">
                            <input type="email" placeholder="Enter your email" className="flex-1 px-6 py-4 bg-white border border-rose-200 text-rose-900 placeholder-rose-400 focus:outline-none focus:border-rose-400 transition-colors" />
                            <button className="px-8 py-4 bg-rose-600 text-white text-sm font-semibold tracking-wider uppercase hover:bg-rose-700 transition-colors whitespace-nowrap">Subscribe</button>
                        </div>
                        <p className="text-rose-500/70 text-sm mt-4">Exclusive access. No spam. Unsubscribe anytime.</p>
                    </div>
                </section>

                {/* Instagram */}
                <section className="py-16 lg:py-24 bg-white">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-12">
                            <div className="flex items-center justify-center gap-3 mb-3">
                                <Instagram className="w-6 h-6 text-rose-600" />
                                <span className="text-rose-600 font-medium">@voguefashionindia</span>
                            </div>
                            <h2 className="text-3xl lg:text-4xl font-extralight text-rose-950">Follow Our World</h2>
                        </div>
                        <div className="grid grid-cols-3 md:grid-cols-6 gap-2 lg:gap-4">
                            {[1, 2, 3, 4, 5, 6].map((_, index) => (
                                <div key={index} className="group relative aspect-square overflow-hidden cursor-pointer">
                                    <img src={`https://placehold.co/300x300/FDF2F8/831843?text=Insta+${index + 1}`} alt={`Instagram post ${index + 1}`} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                                    <div className="absolute inset-0 bg-rose-600/0 group-hover:bg-rose-600/30 transition-colors duration-300 flex items-center justify-center">
                                        <Heart className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Services */}
                <section className="py-12 bg-rose-50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                            {[
                                { icon: Truck, title: 'Complimentary Shipping', desc: 'On orders above \u20b925,000' },
                                { icon: RefreshCw, title: 'Hassle-free Returns', desc: 'Within 7 days' },
                                { icon: Shield, title: 'Authenticity Guaranteed', desc: '100% genuine designers' },
                                { icon: Clock, title: 'Personal Styling', desc: 'Expert consultation' }
                            ].map((item, i) => (
                                <div key={i} className="flex items-center gap-4">
                                    <div className="w-12 h-12 bg-white rounded-full flex items-center justify-center shadow-sm">
                                        <item.icon className="w-5 h-5 text-rose-600" />
                                    </div>
                                    <div>
                                        <p className="font-medium text-rose-950">{item.title}</p>
                                        <p className="text-rose-600/70 text-sm">{item.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

            </div>

        </div>
    );
};

export default Template7;
